// JournalEntryDraggableDialog.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Button,
  TextField,
  Autocomplete,
  Portal,
  Popper,
  FormControlLabel,
  Checkbox,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import { getVatByCaseType } from '../../../Utils/GetVatValue';
import { ToastContainer, toast } from 'react-toastify';
import AddAccountCardModal from '../../Widgets/AddAccountCardModal/AddAccountCardModal';
import Constants from '../../../assests/Constants/constants';
import useCaseSystemAccountSettings from '../../../hooks/useCaseSystemAccountSettings';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/system';
import ForeignCurrencyService from '../../../Services/foreignCurrency.service';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import ProjectsService from '../../../Services/projects.service';
import ProjectModal from '../../Projects/ProjectModal.jsx';
import FixedSizeVirtualizedListbox from '../../Widgets/AutocompleteCardSelect/FixedSizeVirtualizedListbox';

function getAssignedProjectIdFromItem(item) {
  if (!item) return '';
  const p = item?.projects?.[0] ?? item?.segmentations?.[0];
  if (p) {
    const id = p.projectId ?? p.ProjectId ?? p.id ?? p.Id;
    if (id != null && id !== '') return String(id);
  }
  const segId =
    item.segmentationId ??
    item.SegmentationId ??
    item.segmentationProjectId ??
    item.projectId;
  if (segId != null && segId !== '') return String(segId);
  return '';
}

function normalizeSegmentationProjectRow(p) {
  const rawId = p?.id ?? p?.Id ?? p?.projectId ?? p?.ProjectId;
  const id = rawId != null && rawId !== '' ? String(rawId) : '';
  return {
    ...p,
    id,
    code: p?.code ?? p?.Code ?? '',
    name: p?.name ?? p?.Name ?? '',
  };
}

const DragOverlayText = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#333',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  padding: '10px 20px',
  borderRadius: '5px',
  display: 'none',
});

const DropZoneContainer = styled('div')(({ isDragActive }) => ({
  // border: '2px dashed #ccc',
  width: '100%',
  textAlign: 'center',
  cursor: 'pointer',
  background: isDragActive ? '#e0e0e0' : 'transparent',
  transition: 'background 0.3s ease-in-out',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'relative',
}));

const StyledListItem = styled('li')({
  '&:hover': { backgroundColor: '#F5F5F6', cursor: 'pointer' },
});

const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 'var(--Spacing-md, 8px)',
    background: 'var(--white, #FFF)',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
  },
  '& .MuiAutocomplete-listbox': {
    padding: theme.spacing(1),
  },
  '& .MuiAutocomplete-option': {
    borderRadius: 'var(--Spacing-sm, 4px)',
    '&[aria-selected="true"], &.Mui-focused, &:hover': {
      background: 'var(--orange, #F9AA2A) !important',
    },
  },
}));

function JournalEntryDraggableDialog(props) {
  const { t, i18n } = useTranslation();

  // ----------------------
  // Destructure all props from parent
  // ----------------------
  const {
    open,
    onClose,
    data,
    onSave,
    // Arrays with default to empty to avoid "undefined" issues:
    accountCardOptions = [],
    creditAccountOptions = [],
    debitAccountOptions = [],
    sortCodeIdOptions = [],
    // Additional props
    direction = 'rtl',
    isAddMode = false,
    isClosedReportingMonth = false,
    client = {},
    token = '',
    setLoading = () => { },
    // The ones you specifically requested:
    isRange = false,
    palCodes = [],
    palCode = '',
    setPalCode = () => { },
    showAccountCardInputsEdit = false,
    fetchEnums = () => { },
    sortDate = null, // Add sortDate prop
  } = props;

  const { settings: caseSystemSettings } = useCaseSystemAccountSettings(client?.caseId, token);

  const [formData, setFormData] = useState({
    ...data,
    journalEntryType: data?.journalEntryType ?? 0, // Default to NormalJournal (0)
    exchangeRateType: data?.exchangeRateType ?? Constants.ExchangeRateType?.Representative ?? 0,
    exchangeRate: data?.exchangeRate ?? null,
  });
  
  // Reset form data when data prop changes (for new entries)
  useEffect(() => {
    setFormData({
      ...data,
      journalEntryType: data?.journalEntryType ?? 0, // Default to NormalJournal (0)
      exchangeRateType: data?.exchangeRateType ?? Constants.ExchangeRateType?.Representative ?? 0,
      exchangeRate: data?.exchangeRate ?? null,
    });
    // Reset VAT selection when form is reset
    setSelectedVatValue(data?.vat || 0);
    // Reset documents when form is reset (when we get a new empty form)
    if (data && (!data.documents || data.documents.length === 0)) {
      setDocuments([]);
    }
    // Reset error state when form is reset
    setHasSameAccountError(false);
  }, [data]);
  
  useEffect(() => {
    setFormData((prev) => ({
      foreignCurrencyCode: '',
      foreignCurrencyAmount: '',
      // Set registrationMonthDate and reportingMonthDate from sortDate if available
      registrationMonthDate: sortDate ? sortDate: prev.registrationMonthDate,
      reportingMonthDate: sortDate ? sortDate : prev.reportingMonthDate,
      ...prev,
    }));
  }, [sortDate]);             // run once on mount and when sortDate changes
  const [documents, setDocuments] = useState([]);

  const MANUAL_RATE_DECIMALS = 10;
  const [FCRate, setFCRate] = useState(1);
  const [isLoadingCurrencyRate, setIsLoadingCurrencyRate] = useState(false);
  const [manualRateInput, setManualRateInput] = useState(formData.exchangeRate ?? '');
  const lastManualRateRef = useRef(null);
  const userClearedManualRateRef = useRef(false);
  const exchangeRateType = formData.exchangeRateType ?? Constants.ExchangeRateType?.Representative ?? 0;
  const isManualRate = exchangeRateType === Constants.ExchangeRateType?.Manual;

  // We create a container for the Portal (for Draggable usage)
  const [container] = useState(() => document.createElement('div'));
  const nodeRef = useRef(null);

  // For VAT
  const [selectedVatValue, setSelectedVatValue] = useState(formData?.vat || 0);

  // For controlling AddAccountCard logic
  const [searchTermCredit, setSearchTermCredit] = useState('');
  const [searchTermDebit, setSearchTermDebit] = useState('');
  const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
  const [isEditAccountCard, setIsEditAccountCard] = useState(false);

  // For tracking validation errors
  const [hasSameAccountError, setHasSameAccountError] = useState(false);

  const showProjectAssignRow = isAddMode && Boolean(client?.caseId && token && client?.managesProject);
  const [segmentationProjects, setSegmentationProjects] = useState([]);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectCreateLoading, setProjectCreateLoading] = useState(false);
  const projectAutocompleteOptions = useMemo(
    () => [
      { id: '__ADD_NEW__', label: t('AddProject'), kind: 'add' },
      ...segmentationProjects.map((p) => ({
        id: String(p.id ?? ''),
        label: `${p.code} — ${p.name}`,
        kind: 'project',
      })),
    ],
    [segmentationProjects, t]
  );

  const assignedProjectIdStr = useMemo(
    () => getAssignedProjectIdFromItem(formData),
    [formData]
  );

  const selectedProjectAutocompleteValue = useMemo(() => {
    if (!assignedProjectIdStr) return null;
    const matchedOption = projectAutocompleteOptions.find(
      (o) => o.kind === 'project' && String(o.id) === String(assignedProjectIdStr)
    );
    if (matchedOption) return matchedOption;

    const code = formData.segmentationCode ?? formData.SegmentationCode ?? '';
    const name = formData.segmentationName ?? formData.SegmentationName ?? '';
    if (!code && !name) return null;

    return {
      id: String(assignedProjectIdStr),
      label: `${code} — ${name}`.replace(/^ — | — $/g, '').trim() || String(assignedProjectIdStr),
      kind: 'project',
    };
  }, [
    assignedProjectIdStr,
    projectAutocompleteOptions,
    formData.segmentationCode,
    formData.SegmentationCode,
    formData.segmentationName,
    formData.SegmentationName,
  ]);

  const fetchSegmentationProjects = useCallback(async () => {
    if (!client?.caseId || !token) {
      setSegmentationProjects([]);
      return;
    }
    try {
      const res = await ProjectsService.getProjects(client.caseId, token);
      if (!res.ok) return;
      const data = await res.json();
      const list = (Array.isArray(data) ? data : [])
        .map(normalizeSegmentationProjectRow)
        .filter((p) => p.id);
      setSegmentationProjects(list);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [client?.caseId, token]);

  useEffect(() => {
    if (!showProjectAssignRow) return;
    void fetchSegmentationProjects();
  }, [showProjectAssignRow, fetchSegmentationProjects]);

  const applyProjectToFormData = useCallback((newSegmentationId, optionalProjectMeta = null) => {
    const newId = newSegmentationId ? String(newSegmentationId) : '';
    const meta =
      optionalProjectMeta ||
      segmentationProjects.find((p) => String(p.id) === newId);
    setFormData((prev) => ({
      ...prev,
      projects: newId
        ? [{ projectId: newId, code: meta?.code ?? '', name: meta?.name ?? '' }]
        : [],
      segmentationId: newId || null,
      segmentationCode: newId ? meta?.code ?? '' : null,
      segmentationName: newId ? meta?.name ?? '' : null,
    }));
  }, [segmentationProjects]);

  const handleProjectAutocompleteChange = (_event, newValue) => {
    if (newValue?.kind === 'add' || newValue?.id === '__ADD_NEW__') {
      setProjectModalOpen(true);
      return;
    }
    if (!newValue || newValue.kind === 'none' || newValue.id === '') {
      applyProjectToFormData('');
      return;
    }
    applyProjectToFormData(String(newValue.id));
  };

  const handleProjectModalClose = () => {
    setProjectModalOpen(false);
  };

  const handleProjectModalSave = async (projectInput) => {
    if (!client?.caseId || !token) return false;
    const drafts = Array.isArray(projectInput) ? projectInput : [projectInput];
    const normalized = drafts.map((d) => ({
      code: String(d?.code ?? '').trim(),
      name: String(d?.name ?? '').trim(),
    }));
    if (normalized.some((d) => !d.code || !d.name)) return false;

    setProjectCreateLoading(true);
    try {
      let lastCreated = null;
      for (const draft of normalized) {
        const res = await ProjectsService.createProject(
          { CaseId: client.caseId, Code: draft.code, Name: draft.name },
          token
        );
        if (!res.ok) {
          const msg = await res.text();
          toast.error(msg || t('ErrorCreatingProject'));
          await fetchSegmentationProjects();
          return false;
        }
        lastCreated = await res.json();
      }
      toast.success(`${t('Add')} ${t('Projects')}`);
      await fetchSegmentationProjects();
      setProjectModalOpen(false);
      if (lastCreated?.id) {
        applyProjectToFormData(String(lastCreated.id), {
          code: lastCreated.code,
          name: lastCreated.name,
        });
      }
      return true;
    } catch (e) {
      console.error(e);
      toast.error(t('ErrorCreatingProject'));
      return false;
    } finally {
      setProjectCreateLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {

    setIsDragging(false);
    if (acceptedFiles.length > 0) {
      // Convert to a proper array
      const newDocuments = [...documents, ...acceptedFiles];
      // Ensure setDocuments receives an array
      setDocuments(newDocuments);
    }
  }, [documents, setDocuments]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setFormData(curr => ({ ...curr, documents }));
  }, [documents]);


  // The new account card data that will be passed to AddAccountCardModal
  const [newAccountCard, setNewAccountCard] = useState({
    accountCodeName: '',
    accountCodeId: '',
    accountCaseId: '',
  });

  /** Convert FC → ILS and store in formData.amount. When Manual uses manual rate (no API). */
  const recalcAmount = async (foreignCurrencyAmount, code = null, date = null) => {
    if (foreignCurrencyAmount == null || foreignCurrencyAmount === '') return;
    if (!code && (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '')) {
      toast.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
      return;
    }
    const fcNum = Number(foreignCurrencyAmount);
    if (isNaN(fcNum) || fcNum <= 0) return;
    const manual = (formData.exchangeRateType ?? Constants.ExchangeRateType?.Representative) === Constants.ExchangeRateType?.Manual;
    const manualRate = manual ? Number(formData.exchangeRate || manualRateInput) : null;
    if (manual) {
      if (manualRate > 0 && !isNaN(manualRate)) {
        const amount = Number((fcNum * manualRate).toFixed(2));
        setFormData((prev) => ({ ...prev, amount }));
      }
      return;
    }
    if (!(date ?? formData.documentDate)) {
      toast.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
      return;
    }
    const fxCode = code ?? formData.foreignCurrencyCode;
    const fxDate = date ?? formData.documentDate;
    try {
      const rate = await ForeignCurrencyService.GetIlsRateForDate(fxCode, fxDate, token);
      const amount = Number((fcNum * rate).toFixed(2));
      setFormData((prev) => ({ ...prev, amount }));
    } catch (err) {
      console.error('Failed to recalc FC amount', err);
      toast.error(t('CurrencyRateFetchError'));
    }
  };

  /** Convert ILS amount → FC amount (for when user changes amount). */
  const recalcForeignAmount = async (ilsAmount, code = null, date = null) => {
    if (ilsAmount == null || ilsAmount === '') return;
    if (!code && (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '')) return;
    const amountNum = Number(ilsAmount);
    if (isNaN(amountNum) || amountNum <= 0) return;
    const manual = (formData.exchangeRateType ?? Constants.ExchangeRateType?.Representative) === Constants.ExchangeRateType?.Manual;
    const manualRate = manual ? Number(formData.exchangeRate || manualRateInput) : null;
    if (manual) {
      if (manualRate > 0 && !isNaN(manualRate)) {
        const foreignAmount = Number((amountNum / manualRate).toFixed(2));
        setFormData((prev) => ({ ...prev, foreignCurrencyAmount: foreignAmount }));
      }
      return;
    }
    if (!(date ?? formData.documentDate)) return;
    const fxCode = code ?? formData.foreignCurrencyCode;
    const fxDate = date ?? formData.documentDate;
    try {
      const rate = await ForeignCurrencyService.GetIlsRateForDate(fxCode, fxDate, token);
      const foreignAmount = Number((amountNum / rate).toFixed(2));
      setFormData((prev) => ({ ...prev, foreignCurrencyAmount: foreignAmount }));
    } catch (err) {
      console.error('Failed to recalc FC amount', err);
      toast.error(t('CurrencyRateFetchError'));
    }
  };

  /** Wrapper for the FC amount field */
  const handleForeignAmountChange = (val) => {
    if (isManualRate) userClearedManualRateRef.current = false;
    setFormData((prev) => ({ ...prev, foreignCurrencyAmount: val }));
    if (formData.foreignCurrencyCode === null || formData.foreignCurrencyCode === '' || formData.foreignCurrencyCode === undefined) {
      toast.error(t('PleaseSelectCurrencyCodeFirst'));
      return;
    }
    recalcAmount(val);
  };

  const handleManualRateChange = (val) => {
    setFormData((prev) => ({ ...prev, exchangeRate: val }));
    if (val !== '' && !isNaN(Number(val)) && Number(val) > 0) lastManualRateRef.current = val;
    if (val === '' || isNaN(Number(val)) || Number(val) <= 0 || formData.amount == null || formData.amount === '') return;
    if (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '' || formData.foreignCurrencyCode === undefined) {
      toast.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
      return;
    }
    setFormData((prev) => ({ ...prev, foreignCurrencyAmount: (Number(prev.amount) / Number(val)).toFixed(2) }));
  };

  const getManualRateDisplayState = () => {
    const amountNum = Number(formData.amount);
    const fcNum = Number(formData.foreignCurrencyAmount);
    const hasForeignCode = formData.foreignCurrencyCode != null && formData.foreignCurrencyCode !== '';
    const hasValidAmountAndFc = hasForeignCode && amountNum > 0 && fcNum > 0 && !isNaN(amountNum) && !isNaN(fcNum);
    const calculatedManualRate = hasValidAmountAndFc ? (amountNum / fcNum).toFixed(MANUAL_RATE_DECIMALS) : null;
    const manualRateDisplayValue = (manualRateInput !== '' && manualRateInput != null)
      ? manualRateInput
      : (userClearedManualRateRef.current ? '' : (calculatedManualRate ?? ''));
    const hasValidDisplayRate = manualRateDisplayValue !== '' && !isNaN(Number(manualRateDisplayValue)) && Number(manualRateDisplayValue) > 0;
    const isShowingCalculatedRate = (manualRateInput === '' || manualRateInput == null) && calculatedManualRate != null && !userClearedManualRateRef.current;
    return { amountNum, fcNum, hasForeignCode, hasValidAmountAndFc, calculatedManualRate, manualRateDisplayValue, hasValidDisplayRate, isShowingCalculatedRate };
  };

  useEffect(() => {
    if (!token) return;
    const fetchRate = async () => {
      setIsLoadingCurrencyRate(true);
      try {
        const rate = await ForeignCurrencyService.GetIlsRateForDate(
          formData.foreignCurrencyCode,
          formData.documentDate,
          token
        );
        setFCRate(rate);
        const amountRaw = formData.amount;
        if (rate != null && rate > 0 && amountRaw != null && amountRaw !== '' && !isNaN(Number(amountRaw)) && !isManualRate) {
          const amountNum = Number(amountRaw);
          setFormData((prev) => ({ ...prev, amount: Number(amountNum.toFixed(2)), foreignCurrencyAmount: (amountNum / rate).toFixed(2) }));
        }
      } catch (err) {
        console.error(err);
        toast.error(t('CurrencyRateFetchError'));
      } finally {
        setIsLoadingCurrencyRate(false);
      }
    };
    if (
      formData.foreignCurrencyCode != null && formData.foreignCurrencyCode !== '' &&
      formData.documentDate && !isManualRate
    ) {
      fetchRate();
    } else {
      if (formData.foreignCurrencyCode === '' || formData.foreignCurrencyCode == null) {
        setFCRate(1);
      }
      setIsLoadingCurrencyRate(false);
    }
  }, [formData.foreignCurrencyCode, formData.documentDate, formData.exchangeRateType]);

  useEffect(() => {
    if (!isManualRate) {
      setManualRateInput('');
      userClearedManualRateRef.current = false;
      return;
    }
    if (userClearedManualRateRef.current) {
      setManualRateInput('');
      return;
    }
    const rate = formData.exchangeRate != null && formData.exchangeRate !== ''
      ? String(Number(formData.exchangeRate))
      : (lastManualRateRef.current ?? '');
    setManualRateInput(rate);
    if (rate) lastManualRateRef.current = rate;
  }, [formData.exchangeRateType, formData.exchangeRate]);

  useEffect(() => {
    if (!isManualRate) return;
    if (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '') return;
    if (formData.exchangeRate != null && formData.exchangeRate !== '') return;
    const amount = Number(formData.amount);
    const fc = Number(formData.foreignCurrencyAmount);
    if (!amount || !fc || isNaN(amount) || isNaN(fc) || amount <= 0 || fc <= 0) return;
    lastManualRateRef.current = (amount / fc).toFixed(MANUAL_RATE_DECIMALS);
  }, [formData.exchangeRateType, formData.exchangeRate, formData.amount, formData.foreignCurrencyAmount, formData.foreignCurrencyCode]);



  // Whether we're adding a 'creditAccount' or 'debitAccount'
  const [addAccountCardType, setAddAccountCardType] = useState('creditAccount');

  // If you need an auto-increment cardId
  const [cardIdOption, setCardIdOption] = useState(0);

  // Create enhanced options with "Add New" as first item
  const enhancedDebitAccountOptions = useMemo(() => {
    const addNewOption = {
      cardId: 'ADD_NEW_ACCOUNT',
      name: t('AddAccountCard'),
      isEditable: false,
      isAddNew: true
    };
    return [addNewOption, ...debitAccountOptions];
  }, [debitAccountOptions, t]);

  const enhancedCreditAccountOptions = useMemo(() => {
    const addNewOption = {
      cardId: 'ADD_NEW_ACCOUNT',
      name: t('AddAccountCard'),
      isEditable: false,
      isAddNew: true
    };
    return [addNewOption, ...creditAccountOptions];
  }, [creditAccountOptions, t]);

  // Define required fields
  const requiredFields = ['sortCodeId', 'amount', 'documentDate'];

  // Check if all required fields are filled
  const isSaveDisabled = requiredFields.some((field) => !formData[field]);

  // Determine whether credit/debit account fields should be disabled
  const isCreditDisabled = formData.sortCodeId === caseSystemSettings.sortCodeDebit;
  const isDebitDisabled = formData.sortCodeId === caseSystemSettings.sortCodeCredit;

  // If parent also wants to handle close differently, define a local helper
  const handleCloseItem = () => {
    setEditedAddAccountCard(false);
  };




  // Set up the Draggable center logic
  useEffect(() => {
    if (open) {
      document.body.appendChild(container);
      const updateModalPosition = () => {
        const modalWidth = 700;
        const modalHeight = nodeRef.current ? nodeRef.current.offsetHeight : 0;
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const x = (screenWidth - modalWidth) / 2;
        const y = (screenHeight - modalHeight) / 2;
        if (nodeRef.current) {
          nodeRef.current.style.left = `${x}px`;
          nodeRef.current.style.top = `${y}px`;
        }
      };
      setTimeout(updateModalPosition, 0);
      window.addEventListener('resize', updateModalPosition);
      return () => {
        try {
          document.body.removeChild(container);
        } catch (e) {
          // Ignore
        }
        window.removeEventListener('resize', updateModalPosition);
      };
    } else {
      try {
        document.body.removeChild(container);
      } catch (e) {
        // Ignore
      }
    }
  }, [open, container]);

  // Keep selectedVatValue in sync
  useEffect(() => {
    setSelectedVatValue(formData.vat || 0);
  }, [formData.vat]);

  // If accountCardOptions changes, recalc max cardId
  useEffect(() => {
    if (accountCardOptions.length > 0) {
      const maxCardId = Math.max(
        ...accountCardOptions.map((opt) => parseInt(opt.cardId, 10) || 0)
      );
      setCardIdOption(maxCardId + 1);
    } else {
      setCardIdOption(1);
    }
  }, [accountCardOptions]);

  // Called when user types in an unknown account & clicks "AddAccountCard"
  const handleNoOptionCreditDebitClick = (searchTerm, fieldName) => {
    setEditedAddAccountCard(true);
    setIsEditAccountCard(false);
    setAddAccountCardType(fieldName);
    setNewAccountCard({
      accountCodeName: searchTerm || '',
      accountCodeId: cardIdOption.toString(),
      accountCaseId: '',
    });
  };

  // When the user **successfully adds** a new card in the modal
  // we set that new card ID to the correct field (credit/debit) in our form.
  // AddAccountCardModal already awaited fetchEnums (parent should pass force refetch) before calling this.
  const handleChangeAfterAdd = (newlyAddedCardId, fieldName, addedAccountCaseId) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newlyAddedCardId,
      ...(addedAccountCaseId != null && addedAccountCaseId !== ''
        ? { accountCaseId: addedAccountCaseId }
        : {}),
    }));
  };

  // Options for VAT checkboxes
  const vatOptions = [
    { value: 100, label: '100%' },
    { value: Constants.Vat66, label: '66%' },
    { value: 25, label: '25%' },
    { value: 0, label: '0%' },
    { value: 16, label: t('AccountingTax') },
  ];

  // Called whenever user types in a field
  const handleChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };

    // Validation: Credit and Debit accounts must be different
    if (field === 'creditAccount' && value && value === formData.debitAccount) {
      setHasSameAccountError(true);
      toast.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
      return; // Don't update the state
    }
    if (field === 'debitAccount' && value && value === formData.creditAccount) {
      setHasSameAccountError(true);
      toast.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
      return; // Don't update the state
    }

    // Clear error if accounts are being changed to different values
    if ((field === 'creditAccount' || field === 'debitAccount') && hasSameAccountError) {
      setHasSameAccountError(false);
    }

    // If they change the sortCodeId, apply logic for default vat, credit, or debit
    if (field === 'sortCodeId') {
      const selectedOption = sortCodeIdOptions.find(
        (option) => option.sortCodeId === value
      );
      if (selectedOption) {
        const nameWithoutParentheses = selectedOption.sortCodeName
          .replace(/\s*\(.*?\)\s*/, '')
          .trim();
        const match = selectedOption.sortCodeName.match(/\((\d+)%?/);
        const sortCode = selectedOption;

        // Match your constants logic
        const newVat = sortCode?.vat ?? 100;
        updatedData.vat = newVat;

        // Handle credit/debit accounts based on sortCodeId
        if (value === '1') {
          // Credit disabled (Debit - חובה)
          updatedData.creditAccount = null;
          updatedData.debitAccount = sortCode.debitAccount
            ? sortCode.debitAccount
            : updatedData.debitAccount;
        } else if (value === '2') {
          // Debit disabled (Credit - זכות)
          updatedData.debitAccount = null;
          updatedData.creditAccount = sortCode.creditAccount
            ? sortCode.creditAccount
            : updatedData.creditAccount;
        } else {
          // Normal case: set default credit/debit if provided
          updatedData.creditAccount = sortCode.creditAccount
            ? sortCode.creditAccount
            : updatedData.creditAccount;
          updatedData.debitAccount = sortCode.debitAccount
            ? sortCode.debitAccount
            : updatedData.debitAccount;
        }

        // Automatically set JournalEntryType to IsDepreciationJournal (1) for sort code 905
        if (value === '905') {
          updatedData.journalEntryType = 1; // IsDepreciationJournal
        }
      }
    }
    setFormData(updatedData);
  };

  // Called when user changes the VAT checkbox group
  const handleVatChange = (value) => {
    setSelectedVatValue(value);


    handleChange('vat', value);
  };

  // Called when user hits Save
  const handleSave = () => {
    const isManual = (formData.exchangeRateType ?? Constants.ExchangeRateType?.Representative) === Constants.ExchangeRateType?.Manual;
    const hasValidManualRate = manualRateInput !== '' && !isNaN(Number(manualRateInput)) && Number(manualRateInput) > 0;
    const state = getManualRateDisplayState();
    const effectiveRate = hasValidManualRate ? manualRateInput : (isManual ? state.calculatedManualRate : null);
    const saveData = {
      ...formData,
      documents,
      registrationMonthDate: formData.registrationMonthDate,
      reportingMonthDate: formData.reportingMonthDate,
      journalEntryType:
        formData.journalEntryType != null
          ? formData.journalEntryType
          : (data?.journalEntryType != null ? data.journalEntryType : 0),
      exchangeRateType: formData.exchangeRateType ?? Constants.ExchangeRateType?.Representative ?? 0,
      ...(isManual && effectiveRate ? { exchangeRate: effectiveRate } : {}),
    };
    onSave(saveData);
    setDocuments([]);
  };

  const close = () => {
    setDocuments([]);
    setHasSameAccountError(false);
    onClose();
  }

  /**
   * When user changes the ILS amount, update amount and recalc foreignCurrencyAmount if possible
   */
  const handleChangeAmount = async (value) => {
    if (isManualRate) userClearedManualRateRef.current = false;
    handleChange('amount', value);
    if (
      formData.foreignCurrencyCode != null &&
      formData.foreignCurrencyCode !== '' &&
      formData.foreignCurrencyCode !== undefined &&
      value
    ) {
      recalcForeignAmount(value);
    }
  };

  // MUI Input styling
  const inputPropsStyle = {
    disableUnderline: true,
    className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
      }`,
    sx: {
      height: '40px',
      padding: '0 8px',
      borderRadius: '6px',
      background: '#F5F5F6',
      border: '1px solid transparent',
      '&:focus-within': {
        border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
      },
    },
  };

  // For text alignment inside TextFields
  const inputStyle = {
    style: {
      textAlign: direction === 'ltr' ? 'left' : 'right',
    },
  };

  if (!open) return null;

  return (
    <Portal container={container}>
      <Draggable handle=".handle" nodeRef={nodeRef}>
        <div
          ref={nodeRef}
          style={{
            position: 'absolute',
            top: '10%',
            left: '20%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '10px',
            minHeight: '85vh',
            maxHeight: '95vh',
            overflowY: 'auto',
            minWidth: '800px',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'row',
            direction: direction,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
            border: '1px solid #304fff',
          }}
        >
          {/* Form Content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div
              className="handle"
              style={{
                cursor: 'move',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '6px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                onClick={onClose}
                size="small"
                style={{
                  color: '#666',
                  cursor: 'pointer',
                }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>

              <div className="edit-title">
                {t('AddJournalEntry')}
              </div>

              <div style={{ width: '40px' }}></div>
            </div>

            {/* Modal Content */}
            <div
              className={`form-container ${direction}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
            >
              {/* Row 1 */}
              <div className="form-row">
                {/* Sort Code ID */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Code ID')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <Autocomplete
                    options={sortCodeIdOptions}
                    getOptionLabel={(option) =>
                      `${option.sortCodeId} - ${option.sortCodeName}`
                    }
                    value={
                      sortCodeIdOptions.find(
                        (option) => option.sortCodeId === formData.sortCodeId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      const newSortCodeId = newValue ? newValue.sortCodeId : '';
                      handleChange('sortCodeId', newSortCodeId);
                    }}
                    disabled={isClosedReportingMonth}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('Code ID')}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          ...inputPropsStyle,
                        }}
                        inputProps={{
                          ...params.inputProps,
                          ...inputStyle,
                        }}
                        required
                      />
                    )}
                    PopperComponent={(props) => (
                      <StyledPopper {...props} style={{ ...props.style, direction }} />
                    )}
                  />
                </div>

                {/* Month Date Fields Combined */}
                <div className="edit-form-row-item">
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div className="edit-form-row-item-title">
                        {t('MonthReportDate')}
                      </div>
                      <CustomMonthDatePicker
                        date={
                          formData.reportingMonthDate
                            ? dayjs(new Date(formData.reportingMonthDate))
                            : null
                        }
                        onDateChange={(date) => {
                          if (date) {
                            handleChange('reportingMonthDate', date);
                          }
                        }}
                        height="40px"
                        disabled={isClosedReportingMonth}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="edit-form-row-item-title">
                        {t('RegistrationMonthDate')}
                      </div>
                      <CustomMonthDatePicker
                        date={
                          formData.registrationMonthDate
                            ? dayjs(new Date(formData.registrationMonthDate))
                            : null
                        }
                        onDateChange={(date) => {
                          if (date) {
                            handleChange('registrationMonthDate', date);
                          }
                        }}
                        height="40px"
                        disabled={isClosedReportingMonth}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2 */}
              <div className="form-row">
                {/* Debit Account */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Debit Account')}
                  </div>
                  <Autocomplete
                    options={enhancedDebitAccountOptions}
                    getOptionLabel={(o) => {
                      if (o.isAddNew) return o.name;
                      return `${o.cardId} - ${o.name}`;
                    }}
                    value={enhancedDebitAccountOptions.find((o) => o.cardId === formData.debitAccount) || null}
                    onChange={(e, v) => {
                      if (v && v.isAddNew) {
                        // User clicked the "Add New Account" option
                        handleNoOptionCreditDebitClick('', 'debitAccount');
                        return;
                      }
                      const id = v ? v.cardId : null;
                      handleChange('debitAccount', id);
                    }}
                    onInputChange={(e, v) => setSearchTermDebit(v)}
                    noOptionsText={
                      <Button
                        onClick={() => handleNoOptionCreditDebitClick(searchTermDebit, 'debitAccount')}
                        variant="contained"
                        color="primary"
                      >
                        {t('AddAccountCard')} "{searchTermDebit}"
                      </Button>
                    }
                    disabled={isClosedReportingMonth || isDebitDisabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('Debit Account')}
                        variant="standard"
                        InputProps={{ 
                          ...params.InputProps, 
                          ...inputPropsStyle,
                          sx: {
                            ...inputPropsStyle.sx,
                            border: hasSameAccountError ? '2px solid #d32f2f' : '1px solid transparent',
                          }
                        }}
                        inputProps={{ ...params.inputProps, ...inputStyle }}
                        required
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.isAddNew) {
                        return (
                          <StyledListItem
                            {...props}
                            style={{
                              fontWeight: 'bold',
                              color: '#304FFF',
                              borderBottom: '1px solid #ddd',
                            }}
                          >
                            ➕ {option.name}
                          </StyledListItem>
                        );
                      }
                      return (
                        <Tooltip title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')} placement="top">
                          <StyledListItem
                            {...props}
                            onContextMenu={(event) => {
                              if (!option.isEditable) {
                                event.preventDefault();
                                return;
                              }
                              event.preventDefault();
                              // open AddAccountCardModal in edit mode
                              handleNoOptionCreditDebitClick('', 'debitAccount');
                              setIsEditAccountCard(true);
                              setNewAccountCard({
                                accountCodeName: option.name,
                                accountCodeId: option.cardId,
                                accountCaseId: option.accountCaseId,
                              });
                            }}
                          >
                            {`${option.cardId} - ${option.name}`}
                          </StyledListItem>
                        </Tooltip>
                      );
                    }}
                    PopperComponent={(props) => <Popper {...props} style={{ ...props.style, direction }} />}
                  />
                </div>

                {/* Document Date + Registration Date */}
                <div className="edit-form-row-item">
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div className="edit-form-row-item-title">
                        {t('Document Date')}
                        <span className="required-asterisk">*</span>
                      </div>
                      <CustomDatePicker
                        date={formData.documentDate ? formData.documentDate : null}
                        onDateChange={(date) => {
                          if (date) {
                            const iso = date.toISOString();
                            handleChange('documentDate', iso);
                            recalcAmount(
                              formData.foreignCurrencyAmount,
                              formData.foreignCurrencyCode,
                              iso
                            );
                          }
                        }}
                        height="40px"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="edit-form-row-item-title">
                        {t('RegistrationDate')}
                        <span className="required-asterisk">*</span>
                      </div>
                      <CustomDatePicker
                        date={formData.uploadDate ? new Date(formData.uploadDate) : null}
                        onDateChange={(date) => {
                          if (date) {
                            handleChange('uploadDate', date.toISOString());
                          }
                        }}
                        height="40px"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3 */}
              <div className="form-row">
                {/* Credit Account */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Credit Account')}
                  </div>
                  <Autocomplete
                    options={enhancedCreditAccountOptions}
                    getOptionLabel={(o) => {
                      if (o.isAddNew) return o.name;
                      return `${o.cardId} - ${o.name}`;
                    }}
                    value={enhancedCreditAccountOptions.find((o) => o.cardId === formData.creditAccount) || null}
                    onChange={(e, v) => {
                      if (v && v.isAddNew) {
                        // User clicked the "Add New Account" option
                        handleNoOptionCreditDebitClick('', 'creditAccount');
                        return;
                      }
                      const id = v ? v.cardId : null;
                      handleChange('creditAccount', id);
                    }}
                    onInputChange={(e, v) => setSearchTermCredit(v)}
                    noOptionsText={
                      <Button
                        onClick={() => handleNoOptionCreditDebitClick(searchTermCredit, 'creditAccount')}
                        variant="contained"
                        color="primary"
                      >
                        {t('AddAccountCard')} "{searchTermCredit}"
                      </Button>
                    }
                    disabled={isClosedReportingMonth || isCreditDisabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('Credit Account')}
                        variant="standard"
                        InputProps={{ 
                          ...params.InputProps, 
                          ...inputPropsStyle,
                          sx: {
                            ...inputPropsStyle.sx,
                            border: hasSameAccountError ? '2px solid #d32f2f' : '1px solid transparent',
                          }
                        }}
                        inputProps={{ ...params.inputProps, ...inputStyle }}
                        required
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.isAddNew) {
                        return (
                          <StyledListItem
                            {...props}
                            style={{
                              fontWeight: 'bold',
                              color: '#304FFF',
                              borderBottom: '1px solid #ddd',
                            }}
                          >
                            ➕ {option.name}
                          </StyledListItem>
                        );
                      }
                      return (
                        <Tooltip
                          title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')}
                          placement="top"
                        >
                          <StyledListItem
                            {...props}
                            onContextMenu={(event) => {
                              if (!option.isEditable) {
                                event.preventDefault();
                                return;
                              }
                              event.preventDefault();
                              // open AddAccountCardModal pre-populated for editing this card
                              handleNoOptionCreditDebitClick('', 'creditAccount');
                              setIsEditAccountCard(true);
                              setNewAccountCard({
                                accountCodeName: option.name,
                                accountCodeId: option.cardId,
                                accountCaseId: option.accountCaseId,
                              });
                            }}
                          >
                            {`${option.cardId} - ${option.name}`}
                          </StyledListItem>
                        </Tooltip>
                      );
                    }}
                    PopperComponent={(props) => (
                      <Popper {...props} style={{ ...props.style, direction }} />
                    )}
                  />
                </div>

                {showProjectAssignRow && (
                  <div className="edit-form-row-item" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div className="edit-form-row-item-title">{t('Projects')}</div>
                    <Autocomplete
                      value={selectedProjectAutocompleteValue}
                      onChange={handleProjectAutocompleteChange}
                      options={projectAutocompleteOptions}
                      getOptionLabel={(option) => option?.label ?? ''}
                      isOptionEqualToValue={(a, b) => {
                        if (!a && !b) return true;
                        if (!a || !b) return false;
                        return a.kind === b.kind && String(a.id) === String(b.id);
                      }}
                      filterOptions={(options, state) => {
                        const q = state.inputValue.trim().toLowerCase();
                        if (!q) return options;
                        return options.filter(
                          (o) => o.kind === 'add' || o.label.toLowerCase().includes(q)
                        );
                      }}
                      disabled={isClosedReportingMonth || projectCreateLoading}
                      disableListWrap
                      ListboxComponent={FixedSizeVirtualizedListbox}
                      ListboxProps={{ direction, itemHeight: 55, listHeight: 280 }}
                      PopperComponent={(props) => (
                        <StyledPopper
                          {...props}
                          style={{ ...props.style, direction }}
                        />
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('Projects')}
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            ...inputPropsStyle,
                            sx: {
                              ...inputPropsStyle.sx,
                              border: '1px solid transparent',
                            },
                          }}
                          inputProps={{ ...params.inputProps, ...inputStyle }}
                        />
                      )}
                      renderOption={(props, option) => {
                        if (option?.kind === 'add') {
                          return (
                            <StyledListItem
                              {...props}
                              key={props.key}
                              style={{
                                ...(props.style || {}),
                                textAlign: direction === 'rtl' ? 'right' : undefined,
                                fontWeight: 600,
                                color: 'var(--Foundation-Blue-Normal, #304FFF)',
                              }}
                            >
                              {option.label}
                            </StyledListItem>
                          );
                        }
                        return (
                          <StyledListItem
                            {...props}
                            key={props.key}
                            style={{
                              ...(props.style || {}),
                              textAlign: direction === 'rtl' ? 'right' : undefined,
                            }}
                          >
                            {option.label}
                          </StyledListItem>
                        );
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#f5f5f6',
                        },
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Row 4 */}
              <div className="form-row">
                {/* CompanyId */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('CompanyId')}
                    {isAddMode && <span className="required-asterisk"></span>}
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('CompanyId')}
                    value={formData.accountCaseId || ''}
                    onChange={(e) => handleChange('accountCaseId', e.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    disabled={isClosedReportingMonth}
                  />
                </div>

                {/* VAT Percentage */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('VATPercentage')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <FormControl fullWidth variant="standard">
                    <Select
                      fullWidth
                      value={
                        client?.caseType && getVatByCaseType(client.caseType) !== undefined
                          ? getVatByCaseType(client.caseType)
                          : (selectedVatValue !== null && selectedVatValue !== undefined) ? parseInt(selectedVatValue) : 0 // Use the selectedVatValue state
                      }
                      onChange={(e) => {
                        if (
                          client?.caseType &&
                          getVatByCaseType(client.caseType) !== undefined
                        ) {
                          // Prevent change and show toast
                          toast.error(`${t('VAT')} ${client.caseType}`, {
                            autoClose: 3000,
                          });
                        } else {
                          handleVatChange(parseInt(e.target.value)); // Update the selectedVatValue state
                        }
                      }}
                      disableUnderline
                      className="custom-input-box-sizing-toggle"
                      sx={{
                        height: '40px',
                        padding: '0 8px',
                        borderRadius: '6px',
                        background: '#F5F5F6',
                        border: '1px solid transparent',
                        '&:focus-within': {
                          border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                        },
                        textAlign: direction === 'ltr' ? 'right' : 'left',
                      }}
                      inputProps={{
                        style: {
                          textAlign: direction === 'ltr' ? 'right' : 'left',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            direction: direction,
                          },
                        },
                      }}
                      required
                      disabled={isClosedReportingMonth} // Disable if the reporting month is closed
                    >
                      {vatOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              {/* Row 5 */}
              <div className="form-row">
                {/* Reference */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Reference')}
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('Reference')}
                    value={formData.reference || ''}
                    onChange={(e) => handleChange('reference', e.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth}
                  />
                </div>

                {/* Amount */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Amount')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('Amount')}
                    value={formData.amount || ''}
                    onChange={(e) => handleChangeAmount(e.target.value)}
                    variant="standard"
                    type="number"
                    InputProps={{
                      ...inputPropsStyle,
                      sx: {
                        ...inputPropsStyle.sx,
                        border: (!formData.amount || formData.amount === '' || isNaN(Number(formData.amount)) || Number(formData.amount) === 0)
                          ? '2px solid #d32f2f'
                          : '1px solid transparent',
                      },
                    }}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth}
                  />
                </div>
              </div>

              {/* Row 6 */}
              <div className="form-row">
                {/* Reference2 */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Reference2')}
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('Reference2')}
                    value={formData.reference2 || ''}
                    onChange={(e) => handleChange('reference2', e.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth}
                  />
                </div>

                {/* ConfirmationNumber */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('ConfirmationNumber')}
                    <span className="required-asterisk"></span>
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('ConfirmationNumber')}
                    value={formData.confirmationNumber || ''}
                    onChange={(e) => handleChange('confirmationNumber', e.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth}
                  />
                </div>
              </div>

              {/* Row 7 */}
              <div className="form-row">
                {/* Description */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('Description')}
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('Description')}
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    variant="standard"
                    InputProps={inputPropsStyle}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth}
                  />
                </div>

                {/* Currency code + Exchange Rate Type toggle */}
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t('Currency')}
                    <div style={{ display: 'flex', gap: '2px', height: '20px', padding: '1px', borderRadius: '4px', background: '#F8F9FA', border: '1px solid #E9ECEF' }}>
                      <div
                        onClick={() => {
                          if (!isClosedReportingMonth) {
                            if (manualRateInput !== '' && !isNaN(Number(manualRateInput)) && Number(manualRateInput) > 0) lastManualRateRef.current = manualRateInput;
                            setFormData((p) => ({ ...p, exchangeRateType: Constants.ExchangeRateType?.Representative ?? 0, exchangeRate: null }));
                            setManualRateInput('');
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 6px', borderRadius: '3px', cursor: isClosedReportingMonth ? 'not-allowed' : 'pointer',
                          backgroundColor: exchangeRateType !== Constants.ExchangeRateType?.Manual ? '#304FFF' : 'transparent',
                          color: exchangeRateType !== Constants.ExchangeRateType?.Manual ? '#FFFFFF' : '#6C757D', fontWeight: exchangeRateType !== Constants.ExchangeRateType?.Manual ? '600' : '500', fontSize: '11px',
                        }}
                      >
                        {t('RepresentativeRate')}
                      </div>
                      <div
                        onClick={() => {
                          if (!isClosedReportingMonth) {
                            let restored = formData.exchangeRate != null && formData.exchangeRate !== '' ? String(Number(formData.exchangeRate)) : (lastManualRateRef.current ?? '');
                            if (!restored) {
                              const amount = Number(formData.amount);
                              const fc = Number(formData.foreignCurrencyAmount);
                              if (amount > 0 && fc > 0 && !isNaN(amount) && !isNaN(fc)) restored = (amount / fc).toFixed(MANUAL_RATE_DECIMALS);
                            }
                            setManualRateInput(restored);
                            setFormData((p) => ({ ...p, exchangeRateType: Constants.ExchangeRateType?.Manual ?? 1, exchangeRate: restored || null }));
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 6px', borderRadius: '3px', cursor: isClosedReportingMonth ? 'not-allowed' : 'pointer',
                          backgroundColor: exchangeRateType === Constants.ExchangeRateType?.Manual ? '#304FFF' : 'transparent',
                          color: exchangeRateType === Constants.ExchangeRateType?.Manual ? '#FFFFFF' : '#6C757D', fontWeight: exchangeRateType === Constants.ExchangeRateType?.Manual ? '600' : '500', fontSize: '11px',
                        }}
                      >
                        {t('ManualRate')}
                      </div>
                    </div>
                  </div>
                  <FormControl fullWidth variant="standard">
                    <Select
                      fullWidth
                      value={formData.foreignCurrencyCode ?? ''}
                      onChange={(e) => {
                        const code = e.target.value === '' ? '' : Number(e.target.value);
                        if (code === '' || code === null) {
                          setFormData((p) => ({
                            ...p,
                            foreignCurrencyCode: '',
                            foreignCurrencyAmount: '',
                            amount: '',
                            exchangeRate: '',
                          }));
                          setManualRateInput('');
                          userClearedManualRateRef.current = true;
                          lastManualRateRef.current = null;
                        } else {
                          setFormData((p) => ({ ...p, foreignCurrencyCode: code }));
                          if (formData.amount) recalcForeignAmount(formData.amount, code);
                          else if (formData.foreignCurrencyAmount) recalcAmount(formData.foreignCurrencyAmount, code);
                        }
                      }}
                      disableUnderline
                      className="custom-input-box-sizing-toggle"
                      sx={{
                        height: '40px',
                        padding: '0 8px',
                        borderRadius: '6px',
                        background: '#F5F5F6',
                        border: (exchangeRateType === Constants.ExchangeRateType?.Manual && (formData.foreignCurrencyCode == null || formData.foreignCurrencyCode === '')) ? '2px solid #d32f2f' : '1px solid transparent',
                        '&:focus-within': { border: '2px solid var(--Foundation-Blue-Normal, #304FFF)' },
                        textAlign: direction === 'ltr' ? 'right' : 'left',
                      }}
                      inputProps={{ style: { textAlign: direction === 'ltr' ? 'right' : 'left' } }}
                      MenuProps={{ PaperProps: { style: { direction } } }}
                      required
                      disabled={isClosedReportingMonth}
                    >
                      <MenuItem value="">{t('Clear')}</MenuItem>
                      {Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => (
                        <MenuItem key={num} value={num}>
                          {`   ${code}   `}
                          <span style={{ marginRight: 8 }}>{Constants.ForeignCurrencySymbol[code]}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              {/* Row 8: Currency Amount + Manual Exchange Rate (when Manual) */}
              <div className="form-row">
                <div className="edit-form-row-item">
                  <div className="edit-form-row-item-title">
                    {t('CurrencyAmount')}
                    <span className="required-asterisk">*</span>
                  </div>
                  <TextField
                    fullWidth
                    placeholder={t('CurrencyAmount')}
                    value={formData.foreignCurrencyAmount || ''}
                    onChange={(e) => handleForeignAmountChange(e.target.value)}
                    variant="standard"
                    type="number"
                    InputProps={{
                      ...inputPropsStyle,
                      endAdornment: isLoadingCurrencyRate ? (
                        <InputAdornment position="end">
                          <CircularProgress size={22} />
                        </InputAdornment>
                      ) : null,
                    }}
                    inputProps={inputStyle}
                    required
                    disabled={isClosedReportingMonth || isLoadingCurrencyRate}
                  />
                </div>
                {isManualRate && (() => {
                  const state = getManualRateDisplayState();
                  const helperText = state.isShowingCalculatedRate ? t('CalculatedValue') : !state.hasValidAmountAndFc ? t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode') : undefined;
                  return (
                    <div className="edit-form-row-item" >
                      <div className="edit-form-row-item-title">{t('ManualExchangeRate')}</div>
                      <TextField
                        fullWidth
                        placeholder={t('ManualExchangeRate')}
                        value={state.manualRateDisplayValue}
                        helperText={helperText}
                        onFocus={() => {
                          if (userClearedManualRateRef.current) { setManualRateInput(''); return; }
                          if (formData.exchangeRate != null && formData.exchangeRate !== '') setManualRateInput(String(Number(formData.exchangeRate)));
                          else if (state.hasValidAmountAndFc) setManualRateInput(manualRateInput || lastManualRateRef.current || state.calculatedManualRate || '');
                          else setManualRateInput(manualRateInput || '');
                        }}
                        onBlur={(e) => {
                          const val = e.target.value;
                          if (val !== '' && !isNaN(val) && Number(val) > 0) {
                            const formatted = Number(val).toFixed(MANUAL_RATE_DECIMALS);
                            setManualRateInput(formatted);
                            handleManualRateChange(formatted);
                          }
                        }}
                        onChange={(e) => {
                          const v = e.target.value;
                          userClearedManualRateRef.current = (v === '' || v == null);
                          setManualRateInput(v);
                          handleManualRateChange(v);
                        }}
                        variant="standard"
                        type="number"
                        InputProps={{
                          ...inputPropsStyle,
                          sx: { ...inputPropsStyle.sx, border: (exchangeRateType === Constants.ExchangeRateType?.Manual && !state.hasValidDisplayRate) ? '2px solid #d32f2f' : '1px solid transparent' },
                        }}
                        inputProps={inputStyle}
                        disabled={isClosedReportingMonth}
                      />
                    </div>
                  );
                })()}
                {!isManualRate && <div className="edit-form-row-item"></div>}
              </div>

              {/* File Upload — same .form-row / .edit-form-row-item margins as fields above */}
              <div className="form-row" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div
                  className="edit-form-row-item"
                  style={{ flex: '1 1 100%', minWidth: 0, maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}
                >
                  <div
                    {...getRootProps({
                      className: 'journal-entry-file-upload',
                      style: {
                        width: '100%',
                        maxWidth: '100%',
                        margin: 0,
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        minHeight: '100px',
                        height: 'auto',
                        padding: '20px 16px',
                        borderRadius: '8px',
                        border: '2px dashed #ff0000',
                        background: '#F5F5F6',
                        cursor: 'pointer',
                      },
                    })}
                  >
                    <DropZoneContainer isDragActive={isDragActive}>
                      <input {...getInputProps()} />

                      {isDragging && (
                        <DragOverlayText style={{ display: 'block' }}>
                          {t('DropFilesHere')}
                        </DragOverlayText>
                      )}

                      {!isDragging && (
                        <label
                          className="newDocument-upload-label"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%',
                            textAlign: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="upload-icon"
                            viewBox="0 0 58 58"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M29 38.0625C30.001 38.0625 30.8125 37.251 30.8125 36.25V9.73298L34.874 14.4712C35.5253 15.2313 36.6696 15.3193 37.4296 14.6678C38.1896 14.0164 38.2776 12.8721 37.6261 12.1121L30.3761 3.65378C30.0319 3.25204 29.529 3.02084 29 3.02084C28.471 3.02084 27.9681 3.25204 27.624 3.65378L20.3739 12.1121C19.7224 12.8721 19.8104 14.0164 20.5705 14.6678C21.3305 15.3193 22.4747 15.2313 23.1262 14.4712L27.1875 9.73298V36.25C27.1875 37.251 27.999 38.0625 29 38.0625Z"
                              fill="#F9AA2A"
                            />
                          </svg>
                          <span className="upload-text" style={{ textAlign: 'center' }}>
                            {documents?.length > 0
                              ? documents.map((doc) => doc.name).join(', ')
                              : t('DragDropFilesOrClick')}
                          </span>
                        </label>
                      )}
                    </DropZoneContainer>
                  </div>
                </div>
              </div>

              {/* Buttons — same .form-row / .edit-form-row-item horizontal margins as upload */}
              <div className="form-row" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div
                  className="edit-form-row-item"
                  style={{ flex: '1 1 100%', minWidth: 0, maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}
                >
                  <div className="button-container" style={{ marginTop: 0, width: '100%', boxSizing: 'border-box' }}>
                    <div onClick={onClose} className="edit-cancel-button">
                      {t('close')}
                    </div>
                    <div
                      onClick={(isClosedReportingMonth || isSaveDisabled) ? null : handleSave}
                      className="edit-save-button"
                      style={{ opacity: (isClosedReportingMonth || isSaveDisabled) ? 0.5 : 1, pointerEvents: (isClosedReportingMonth || isSaveDisabled) ? 'none' : 'auto' }}
                    >
                      {t('Save')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Draggable>

      {/* AddAccountCardModal with all the extra props */}
      <AddAccountCardModal
        accountCardOptions={accountCardOptions}
        open={editedAddAccountCard}
        onClose={handleCloseItem}
        editedItem={newAccountCard}
        isEdit={isEditAccountCard}
        isRange={isRange}
        isClosedReportingMonth={isClosedReportingMonth}
        palCodes={palCodes}
        propPalCode={palCode}
        setPalCode={setPalCode}
        direction={direction}
        isAdd={!showAccountCardInputsEdit}
        client={client}
        token={token}
        setLoading={setLoading}
        handleChangeAfterAdd={handleChangeAfterAdd}
        fetchEnums={fetchEnums}
        type={addAccountCardType}
      />

      <ProjectModal
        open={projectModalOpen}
        mode="create"
        direction={direction}
        formData={{ code: '', name: '' }}
        existingProjects={segmentationProjects}
        onClose={handleProjectModalClose}
        onSave={handleProjectModalSave}
        loading={projectCreateLoading}
      />
    </Portal>
  );
}

export default JournalEntryDraggableDialog;